import { useState, useMemo } from 'react';
import { toast } from 'sonner';
interface Device {
  id: string;
  name: string;
  equipment_type: string;
  ports_connected: number;
  ports_total: number;
  ports: any[];
}

interface Edge {
  cable_id: string;
  source: string;
  target: string;
  cable_type: string;
  color?: string;
  length_m?: number;
  netbox_id?: number | null;
}

interface ConnectivityGraphTabProps {
  graph: { edges: Edge[]; devices: Device[] } | undefined;
  handleDeleteCable: (cableId: string) => void;
  handleCreateCable: (data: any) => Promise<boolean>;
}

export function ConnectivityGraphTab({ graph, handleDeleteCable, handleCreateCable }: ConnectivityGraphTabProps) {
  const [showCableModal, setShowCableModal] = useState(false);
  const [cableForm, setCableForm] = useState({
    deviceA: '', portA: '', deviceB: '', portB: '', cableType: 'Cat6', color: 'blue', lengthM: '',
  });

  const devices = graph?.devices || [];
  const selectedDeviceA = useMemo(() => devices.find(d => d.id === cableForm.deviceA), [devices, cableForm.deviceA]);
  const selectedDeviceB = useMemo(() => devices.find(d => d.id === cableForm.deviceB), [devices, cableForm.deviceB]);

  const submitCable = async () => {
    if (!cableForm.portA || !cableForm.portB) {
      toast.warning('Выберите порты А и B');
      return;
    }
    const ok = await handleCreateCable({
      port_a_id: cableForm.portA,
      port_b_id: cableForm.portB,
      cable_type: cableForm.cableType,
      color: cableForm.color,
      length_m: cableForm.lengthM ? parseFloat(cableForm.lengthM) : undefined,
    });
    if (ok) setShowCableModal(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold font-mono text-slate-100">Топология сети</h2>
        <button
          onClick={() => setShowCableModal(true)}
          className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-mono font-medium rounded-lg transition-colors shadow-sm"
        >
          + Подключить кабель
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800/60">
        <table className="w-full text-xs font-mono">
          <thead className="bg-slate-950/60 border-b border-slate-800/60">
            <tr>
              <th className="text-left px-3 py-2.5 font-medium text-slate-400">Устройство</th>
              <th className="text-left px-3 py-2.5 font-medium text-slate-400">Тип</th>
              <th className="text-left px-3 py-2.5 font-medium text-slate-400">Порты</th>
              <th className="text-left px-3 py-2.5 font-medium text-slate-400">Статус</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/30">
            {devices.map((d) => (
              <tr key={d.id} className="hover:bg-slate-800/20 transition-colors">
                <td className="px-3 py-2.5 font-medium text-slate-200">{d.name}</td>
                <td className="px-3 py-2.5 text-slate-400">{d.equipment_type}</td>
                <td className="px-3 py-2.5 text-slate-300">{d.ports_connected}/{d.ports_total}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${d.ports_connected > 0 ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]' : 'bg-rose-500'}`} />
                  <span className={d.ports_connected > 0 ? 'text-emerald-400' : 'text-slate-400'}>
                    {d.ports_connected > 0 ? 'Подключено' : 'Не подключено'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {graph && graph.edges.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Кабели</h3>
          <div className="space-y-2">
            {graph.edges.map((e) => (
              <div key={e.cable_id} className="flex items-center justify-between bg-slate-950/40 border border-slate-800/50 rounded-lg px-3 py-2 text-xs font-mono">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: e.color || '#3b82f6' }} />
                  <span className="text-slate-200 font-medium">{e.source} → {e.target}</span>
                  <span className="text-slate-400">[{e.cable_type}]</span>
                  {e.length_m != null && <span className="text-slate-400">({e.length_m}m)</span>}
                  {e.netbox_id ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-950/40 text-cyan-400 border border-cyan-800/60 font-semibold">
                      NetBox #{e.netbox_id}
                    </span>
                  ) : null}
                </div>
                <button onClick={() => handleDeleteCable(e.cable_id)} className="text-rose-400 hover:text-rose-300 p-1 text-sm">✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {devices.length === 0 && (
        <div className="text-center py-12 text-slate-500 font-mono text-xs">Нет устройств. Добавьте оборудование в стойки и создайте порты.</div>
      )}

      {/* Cable modal */}
      {showCableModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-[440px] p-6 text-slate-100 font-sans">
            <h3 className="text-base font-bold font-mono text-slate-100 mb-4">Подключить кабель</h3>
            <div className="space-y-3 font-mono text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Устройство A</label>
                  <select className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs focus:border-cyan-500 outline-none" value={cableForm.deviceA} onChange={(e) => setCableForm({ ...cableForm, deviceA: e.target.value, portA: '' })}>
                    <option value="">-- Выберите устройство --</option>
                    {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Порт A</label>
                  <select className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs disabled:opacity-50 focus:border-cyan-500 outline-none" value={cableForm.portA} disabled={!selectedDeviceA} onChange={(e) => setCableForm({ ...cableForm, portA: e.target.value })}>
                    <option value="">-- Выберите порт --</option>
                    {selectedDeviceA?.ports.map(p => <option key={p.id} value={p.id}>{p.name} ({p.port_type})</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Устройство B</label>
                  <select className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs focus:border-cyan-500 outline-none" value={cableForm.deviceB} onChange={(e) => setCableForm({ ...cableForm, deviceB: e.target.value, portB: '' })}>
                    <option value="">-- Выберите устройство --</option>
                    {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Порт B</label>
                  <select className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs disabled:opacity-50 focus:border-cyan-500 outline-none" value={cableForm.portB} disabled={!selectedDeviceB} onChange={(e) => setCableForm({ ...cableForm, portB: e.target.value })}>
                    <option value="">-- Выберите порт --</option>
                    {selectedDeviceB?.ports.map(p => <option key={p.id} value={p.id}>{p.name} ({p.port_type})</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Тип кабеля</label>
                <select className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs focus:border-cyan-500 outline-none" value={cableForm.cableType} onChange={(e) => setCableForm({ ...cableForm, cableType: e.target.value })}>
                  <option value="Cat6">Cat6</option>
                  <option value="Cat6A">Cat6A</option>
                  <option value="DAC">DAC</option>
                  <option value="fiber">Fiber</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Цвет</label>
                <input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs focus:border-cyan-500 outline-none" value={cableForm.color} onChange={(e) => setCableForm({ ...cableForm, color: e.target.value })} />
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Длина (м)</label>
                <input type="number" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-200 text-xs focus:border-cyan-500 outline-none" value={cableForm.lengthM} onChange={(e) => setCableForm({ ...cableForm, lengthM: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2.5 mt-6 font-mono text-xs">
              <button onClick={() => setShowCableModal(false)} className="px-4 py-2 border border-slate-800 hover:bg-slate-800/40 rounded-lg text-slate-300">Отмена</button>
              <button onClick={submitCable} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg">Создать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
